/**
 * DocumentVisibilityForm — Iter63b-bis-fix
 *
 * Composant factorisé : checkboxes 6 rôles + autocomplete users + note.
 * Réutilisé par : ApproveDialog (modération iter63b-bis), UploadZone (Feature A),
 * DocumentVisibilityModal (Feature B édition).
 *
 * Props :
 *  - visibleTo, setVisibleTo : array de rôles (controlled)
 *  - visibleToSpecificUsers, setVisibleToSpecificUsers : array de user_ids
 *  - note, setNote : string
 *  - chantierId : pour fetch équipe (optionnel — si absent, équipe non rendue)
 *  - equipeOverride : array de membres (optionnel — utile pour tests/iter63b-bis qui charge déjà la liste)
 *  - testIdPrefix : prefix pour data-testid (default "visibility")
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { ROLE_PROJET, libelleRoleProjet } from "../../lib/libelles";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// RP04 — libellés du vocabulaire unique (lib/libelles.js, 09-VOCABULAIRE §4).
export const ROLE_LABELS = {
  archi: ROLE_PROJET.archi,
  moe_principal: ROLE_PROJET.moe_principal,
  moe_membre: ROLE_PROJET.moe_membre,
  consultant: "Partenaires (BET, AMO…)",
  entreprise: "Entreprises",
  client: ROLE_PROJET.client,
};

export const ROLE_BADGES = {
  archi: "bg-[#1E3A5F] text-white",
  moe_principal: "bg-[#2D6A4F] text-white",
  moe_membre: "bg-[#6B8F71] text-white",
  consultant: "bg-[#8B6F47] text-white",
  entreprise: "bg-[#C75B39] text-white",
  client: "bg-[#6B6B6B] text-white",
};

export const DEFAULT_INTERNAL_VISIBILITY = ["archi", "moe_principal", "moe_membre", "consultant"];

export default function DocumentVisibilityForm({
  visibleTo,
  setVisibleTo,
  visibleToSpecificUsers,
  setVisibleToSpecificUsers,
  note,
  setNote,
  chantierId,
  equipeOverride,
  testIdPrefix = "visibility",
}) {
  const [equipe, setEquipe] = useState(equipeOverride || []);

  useEffect(() => {
    if (equipeOverride !== undefined) {
      setEquipe(equipeOverride);
      return;
    }
    if (!chantierId) return;
    axios
      .get(`${API_URL}/api/projets/${chantierId}/moe`, { withCredentials: true })
      .then((r) => setEquipe(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEquipe([]));
  }, [chantierId, equipeOverride]);

  const toggleRole = (role) => {
    setVisibleTo((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const toggleUser = (userId, checked) => {
    setVisibleToSpecificUsers((prev) =>
      checked ? [...prev, userId] : prev.filter((u) => u !== userId)
    );
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-wide text-[#6B6B6B]">
          Qui peut voir ce document ?
        </Label>
        <div className="space-y-1.5 mt-2">
          {Object.keys(ROLE_LABELS).map((r) => (
            <label
              key={r}
              className="flex items-center gap-2 cursor-pointer hover:bg-[#F8F6F2] px-1 py-0.5 rounded-sm"
            >
              <Checkbox
                checked={visibleTo.includes(r)}
                onCheckedChange={() => toggleRole(r)}
                data-testid={`${testIdPrefix}-role-${r}`}
              />
              <span className="text-sm text-[#2C2C2C]">{ROLE_LABELS[r]}</span>
            </label>
          ))}
        </div>
      </div>

      {equipe && equipe.length > 0 && (
        <div className="border-t border-[#E5E5E5] pt-3">
          <Label className="text-xs uppercase tracking-wide text-[#6B6B6B]">
            Ou cibler des utilisateurs spécifiques
          </Label>
          <div className="space-y-1 mt-2 max-h-32 overflow-y-auto">
            {equipe.filter((m) => m.user_id).map((m) => (
              <label
                key={m.user_id}
                className="flex items-center gap-2 cursor-pointer hover:bg-[#F8F6F2] px-1 py-0.5 rounded-sm"
              >
                <Checkbox
                  checked={visibleToSpecificUsers.includes(m.user_id)}
                  onCheckedChange={(checked) => toggleUser(m.user_id, checked)}
                  data-testid={`${testIdPrefix}-user-${m.user_id}`}
                />
                <span className="text-sm text-[#2C2C2C]">{m.name || m.email}</span>
                {m.role_projet ? (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase ${
                      ROLE_BADGES[m.role_projet] || "bg-[#E0E0DB]"
                    }`}
                  >
                    {libelleRoleProjet(m.role_projet)}
                  </span>
                ) : null}
              </label>
            ))}
          </div>
        </div>
      )}

      {setNote && (
        <div>
          <Label className="text-xs uppercase tracking-wide text-[#6B6B6B]">
            Note interne (optionnelle)
          </Label>
          <textarea
            value={note || ""}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full mt-1 border border-[#E5E5E5] rounded-none p-2 text-sm focus:outline-none focus:border-[#2C2C2C]"
            placeholder="Ex : Visible à toute l'équipe MOE"
            data-testid={`${testIdPrefix}-note`}
          />
        </div>
      )}
    </div>
  );
}
